import React, { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { View } from "views/View";

import { useTranslations } from "../index";
import { TaskViewsContext, TaskViewsContextProps } from "./TaskViewsContext";

const CookieKey = "Tasks:SelectedViewName";

export const TaskViewsProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const i18n = useTranslations();
  const [cookies, setCookie] = useCookies([CookieKey], { doNotParse: true, doNotUpdate: true });
  const [selectedViewName, setSelectedViewName] = useState(cookies[CookieKey] || undefined);

  const views = useMemo<View[]>(
    () => [
      {
        name: "Overview",
        title: i18n.View_Tasks_Overview,
        type: "Grid",
      },
    ],
    [i18n]
  );

  const getView = useCallback(
    (name: string | undefined): View => {
      let view = views.find((x) => x.name == name);
      if (!view) {
        name = "Overview";
        view = views.find((x) => x.name == name);
      }

      if (!view) {
        throw new Error(`View '${name}' not found!`);
      }

      return view;
    },
    [views]
  );

  const handleSetSelectedViewName = useCallback(
    (name: string) => {
      setSelectedViewName(name);
      setCookie(CookieKey, name);
    },
    [setCookie]
  );

  const selectedView = useMemo(() => getView(selectedViewName), [selectedViewName, getView]);

  const value = useMemo<TaskViewsContextProps>(
    () => ({ views, selectedView, setSelectedViewName: handleSetSelectedViewName }),
    [views, selectedView, handleSetSelectedViewName]
  );

  return <TaskViewsContext.Provider value={value}>{children}</TaskViewsContext.Provider>;
};
